import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Flight,
  Place,
  ShowChart,
  TableChart,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAirportDetail } from '../hooks/useMarketData';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Cell,
} from 'recharts';

export const AirportDetail: React.FC = () => {
  const navigate = useNavigate();
  const { marketId, airportCode } = useParams<{ marketId: string; airportCode: string }>();
  const [displayMode, setDisplayMode] = useState<'charts' | 'table'>('charts');
  const [years, setYears] = useState<1 | 3 | 5>(5);
  const [originView, setOriginView] = useState<'country' | 'state'>('country');
  const [excludeDestinationCountry, setExcludeDestinationCountry] = useState(false);

  const { data: detailData, isLoading, error } = useAirportDetail(
    marketId ? parseInt(marketId) : null,
    airportCode || null,
    years
  );

  // Set page title (must be called before any returns - Rules of Hooks)
  usePageTitle(
    detailData?.airport && detailData?.market
      ? `${detailData.airport.airport_name} (${detailData.airport.iata}) - ${detailData.market.name}`
      : 'Airport Detail'
  );

  // Now we can do conditional returns
  if (isLoading) {
    return <LoadingSpinner message="Loading airport traffic data..." />;
  }

  if (error || !detailData) {
    return <ErrorMessage message="Failed to load airport traffic data" />;
  }

  const { market, market_country, airport, timeseries, statistics, covid_baseline, origin_by_country, origin_by_state } = detailData;

  const getTrendIcon = (change: number | null | undefined) => {
    if (change === null || change === undefined) return <TrendingFlat sx={{ fontSize: 16 }} />;
    if (change > 1) return <TrendingUp color="success" sx={{ fontSize: 16 }} />;
    if (change < -1) return <TrendingDown color="error" sx={{ fontSize: 16 }} />;
    return <TrendingFlat sx={{ fontSize: 16 }} />;
  };

  const getChangeColor = (change: number | null | undefined) => {
    if (change === null || change === undefined) return 'text.secondary';
    return change >= 0 ? 'success.main' : 'error.main';
  };

  const formatNumber = (val: number | null | undefined) => {
    if (val === null || val === undefined) return '--';
    if (val >= 1000000) {
      return `${(val / 1000000).toFixed(1)}M`;
    }
    if (val >= 1000) {
      return `${(val / 1000).toFixed(0)}K`;
    }
    return val.toLocaleString('en-US');
  };

  const formatFullNumber = (val: number | null | undefined) => {
    if (val === null || val === undefined) return '--';
    return val.toLocaleString('en-US');
  };

  // Prepare chart data
  const chartData = timeseries.map((item: any) => ({
    date: item.date,
    passengers: item.passengers,
    revpar: item.revpar,
    yoyChange: item.yoy_change,
  }));

  // Get latest data point
  const latestData = timeseries[timeseries.length - 1];

  // Calculate recovery vs 2019
  const covidRecovery = latestData && covid_baseline && covid_baseline > 0
    ? ((latestData.passengers - covid_baseline) / covid_baseline) * 100
    : null;

  // Render statistics card
  const renderStatisticsCard = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Statistical Analysis ({years} Years)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Average Monthly
            </Typography>
            <Typography variant="h6">
              {formatNumber(statistics.average)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              5-Year CAGR
            </Typography>
            <Typography variant="h6" color={getChangeColor(statistics.cagr_5yr)}>
              {statistics.cagr_5yr !== null ? `${statistics.cagr_5yr.toFixed(1)}%` : '--'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Minimum Month
            </Typography>
            <Typography variant="body1">
              {formatNumber(statistics.min)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Maximum Month
            </Typography>
            <Typography variant="body1">
              {formatNumber(statistics.max)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Standard Deviation
            </Typography>
            <Typography variant="body1">
              {formatNumber(statistics.std_dev)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              RevPAR/Pass Corr
            </Typography>
            <Typography variant="body1">
              {statistics.correlation !== null && statistics.correlation !== undefined
                ? statistics.correlation.toFixed(3)
                : '--'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Render COVID recovery card
  const renderCovidRecoveryCard = () => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            COVID Recovery
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {latestData && (
            <>
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  {monthNames[latestData.month - 1]} 2019
                </Typography>
                <Typography variant="h6">
                  {formatNumber(covid_baseline)}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  {monthNames[latestData.month - 1]} {latestData.year}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">
                    {formatNumber(latestData.passengers)}
                  </Typography>
                  {covidRecovery !== null && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getTrendIcon(covidRecovery)}
                      <Typography variant="h5" color={getChangeColor(covidRecovery)}>
                        {covidRecovery > 0 ? '+' : ''}{covidRecovery.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        vs {monthNames[latestData.month - 1]} 2019
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render current performance card
  const renderCurrentPerformanceCard = () => {
    if (!latestData) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Latest Performance
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Chip label={`${latestData.month}/${latestData.year}`} size="small" sx={{ mb: 1 }} />
          <Box display="flex" alignItems="baseline" gap={1} mb={1}>
            <Typography variant="h4" fontWeight="bold">
              {formatNumber(latestData.passengers)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              passengers
            </Typography>
          </Box>

          {latestData.previous_year !== null && latestData.previous_year !== undefined && (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Previous Year: {formatFullNumber(latestData.previous_year)}
              </Typography>
              {latestData.yoy_change !== null && latestData.yoy_change !== undefined && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getTrendIcon(latestData.yoy_change)}
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color={getChangeColor(latestData.yoy_change)}
                  >
                    {latestData.yoy_change > 0 ? '+' : ''}
                    {latestData.yoy_change.toFixed(1)}% YoY
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render historical chart
  const renderHistoricalChart = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Passenger Traffic & RevPAR Trend
          </Typography>
          <ToggleButtonGroup
            value={years}
            exclusive
            onChange={(_, value) => value && setYears(value)}
            size="small"
          >
            <ToggleButton value={1}>1Y</ToggleButton>
            <ToggleButton value={3}>3Y</ToggleButton>
            <ToggleButton value={5}>5Y</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                const [year, month] = value.split('-');
                return `${month}/${year.slice(2)}`;
              }}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => formatNumber(value)}
              label={{ value: 'Passengers', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `$${value.toFixed(0)}`}
              label={{ value: 'RevPAR', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
            />
            <Tooltip
              formatter={(value: any, name: string) => {
                if (name === 'Passengers') {
                  return [formatFullNumber(value), name];
                } else if (name === 'RevPAR') {
                  return [`$${value.toFixed(2)}`, name];
                }
                return [value, name];
              }}
              labelFormatter={(label) => {
                const [year, month] = label.split('-');
                const monthNames = [
                  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                ];
                return `${monthNames[parseInt(month) - 1]} ${year}`;
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="passengers"
              stroke="#1976d2"
              strokeWidth={2}
              dot={false}
              name="Passengers"
              yAxisId="left"
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="revpar"
              stroke="#2e7d32"
              strokeWidth={2}
              dot={false}
              name="RevPAR"
              yAxisId="right"
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );

  // Render origin breakdown chart
  const renderOriginBreakdownChart = () => {
    if (originView === 'country') {
      // Filter and prepare bar chart data (reverse order for horizontal bars to show highest at top)
      let filteredCountries = origin_by_country;
      if (excludeDestinationCountry && market_country) {
        filteredCountries = origin_by_country.filter((item: any) => item.country !== market_country);
      }

      const barData = filteredCountries.map((item: any) => ({
        country: item.country,
        passengers: item.passengers
      })).reverse();

      return (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Passenger Origin Breakdown
              </Typography>
              <ToggleButtonGroup
                value={originView}
                exclusive
                onChange={(_, value) => value && setOriginView(value)}
                size="small"
              >
                <ToggleButton value="country">Country</ToggleButton>
                <ToggleButton value="state">State</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={excludeDestinationCountry}
                    onChange={(e) => setExcludeDestinationCountry(e.target.checked)}
                    size="small"
                  />
                }
                label="Exclude Destination Country"
              />
            </Box>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart data={barData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <YAxis
                  dataKey="country"
                  type="category"
                  tick={{ fontSize: 12 }}
                  width={120}
                />
                <Tooltip formatter={(value: any) => formatFullNumber(value)} />
                <Bar dataKey="passengers" fill="#2e7d32" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    } else {
      // Prepare bar chart data (reverse order for horizontal bars to show highest at top)
      const barData = origin_by_state.map((item: any) => ({
        state: item.state,
        passengers: item.passengers
      })).reverse();

      return (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Passenger Origin Breakdown
              </Typography>
              <ToggleButtonGroup
                value={originView}
                exclusive
                onChange={(_, value) => value && setOriginView(value)}
                size="small"
              >
                <ToggleButton value="country">Country</ToggleButton>
                <ToggleButton value="state">State</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart data={barData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <YAxis
                  dataKey="state"
                  type="category"
                  tick={{ fontSize: 12 }}
                  width={100}
                />
                <Tooltip formatter={(value: any) => formatFullNumber(value)} />
                <Bar dataKey="passengers">
                  {barData.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.state === 'International' ? '#64b5f6' : '#1976d2'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    }
  };

  // Render data table
  const renderDataTable = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" mb={2}>
          Historical Data
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell align="right">Passengers</TableCell>
                <TableCell align="right">Previous Year</TableCell>
                <TableCell align="right">YoY Change</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chartData.slice().reverse().map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{item.date}</TableCell>
                  <TableCell align="right">
                    {formatFullNumber(item.passengers)}
                  </TableCell>
                  <TableCell align="right">
                    {formatFullNumber(timeseries.slice().reverse()[index].previous_year)}
                  </TableCell>
                  <TableCell align="right">
                    {item.yoyChange !== null && item.yoyChange !== undefined ? (
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        {getTrendIcon(item.yoyChange)}
                        <Typography color={getChangeColor(item.yoyChange)} variant="body2">
                          {item.yoyChange > 0 ? '+' : ''}
                          {item.yoyChange.toFixed(1)}%
                        </Typography>
                      </Box>
                    ) : (
                      '--'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  return (
    <Box pb={10}>
      {/* Header */}
      <Box px={2} pt={2} mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate(`/market/${marketId}`)}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Flight sx={{ fontSize: 24, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                {airport.code}
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              {airport.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {market} • {airport.city}{airport.state ? `, ${airport.state}` : ''}
            </Typography>
            {airport.distance_km && (
              <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                <Place sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {airport.distance_km} km from market center
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Controls */}
      <Box px={2} mb={3} display="flex" justifyContent="flex-end" alignItems="center">
        <ToggleButtonGroup
          value={displayMode}
          exclusive
          onChange={(_, value) => value && setDisplayMode(value)}
          size="small"
        >
          <ToggleButton value="charts">
            <ShowChart sx={{ fontSize: 20 }} />
          </ToggleButton>
          <ToggleButton value="table">
            <TableChart sx={{ fontSize: 20 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content */}
      <Box px={2}>
        <Grid container spacing={2}>
          {/* Current Performance */}
          <Grid item xs={12}>
            {renderCurrentPerformanceCard()}
          </Grid>

          {displayMode === 'charts' ? (
            <>
              {/* Historical Chart */}
              <Grid item xs={12}>
                {renderHistoricalChart()}
              </Grid>

              {/* Origin Breakdown Chart */}
              <Grid item xs={12}>
                {renderOriginBreakdownChart()}
              </Grid>

              {/* Statistics */}
              <Grid item xs={12} md={6}>
                {renderStatisticsCard()}
              </Grid>

              {/* COVID Recovery */}
              <Grid item xs={12} md={6}>
                {renderCovidRecoveryCard()}
              </Grid>
            </>
          ) : (
            /* Data Table */
            <Grid item xs={12}>
              {renderDataTable()}
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};
